import { ArrowLeft, ArrowRight, Check, Users } from 'lucide-react';
import {
  canCheckout,
  householdTooSmall,
  minSeats,
  seatsLabel,
  HOUSEHOLD_LABELS,
  LABEL,
  OUTLINE_PLAN_BTN,
  PRIMARY_PLAN_BTN,
} from './helper';

// The subscriber pays, so their row is always on and never togglable — the API
// forces them into the selection regardless of what we send.
const Row = ({ person, checked, disabled, onToggle }) => (
  <label
    className={`flex items-center gap-3 rounded-xl border p-3.5 transition-colors ${
      checked ? 'border-teamcare-blue bg-blue-50/50' : 'border-secondary-200 bg-white'
    } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-secondary-300'}`}
  >
    <input
      type="checkbox"
      className="sr-only"
      checked={checked}
      disabled={disabled}
      onChange={onToggle}
    />
    <span
      aria-hidden
      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 ${
        checked ? 'border-teamcare-blue bg-teamcare-blue text-white' : 'border-secondary-300 bg-white'
      }`}
    >
      {checked && <Check size={13} strokeWidth={3.5} />}
    </span>

    <span className="min-w-0 flex-1 text-sm font-semibold text-secondary-900">{person.name}</span>

    {person.is_subscriber && (
      <span className="shrink-0 rounded-full bg-secondary-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-secondary-500">
        You
      </span>
    )}
  </label>
);

const MemberPicker = ({ plan, household, people, limit, selectedIds, onToggle, onBack, onContinue, busy = false, continueLabel = 'Continue to payment' }) => {
  const tooSmall = householdTooSmall(people, household);
  const full = selectedIds.length >= limit;

  return (
    <div className="animate-slide-up">
      <div className="mb-5 flex items-start gap-4 rounded-xl border border-gray-200 bg-secondary-50/60 p-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-blue-100 bg-white text-teamcare-blue">
          <Users size={20} />
        </span>
        <div className="min-w-0">
          <p className="text-[15px] font-bold text-secondary-900">Who should this membership cover?</p>
          <p className="mt-1 text-xs leading-relaxed text-secondary-500">
            {plan.name} · {HOUSEHOLD_LABELS[household]} covers up to {limit} people. Pick at least{' '}
            {minSeats(household)}.
          </p>
        </div>
      </div>

      {tooSmall ? (
        <div className="rounded-xl border border-amber-100 bg-amber-50 p-4 text-xs leading-relaxed text-amber-800">
          We don't have anyone else on file under your account yet, so this plan can't be filled.
          Ask the practice to link your family members, or choose a single plan for now.
        </div>
      ) : (
        <>
          <div className="mb-2 flex items-center justify-between">
            <p className={LABEL}>Household</p>
            <p className="text-[11px] font-semibold text-secondary-400">
              {seatsLabel(selectedIds.length, limit)}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {people.map((person) => {
              const checked = selectedIds.includes(person.id);

              return (
                <Row
                  key={person.id}
                  person={person}
                  checked={checked}
                  // Locked once the plan is full, so the count can only come down.
                  disabled={person.is_subscriber || busy || (full && !checked)}
                  onToggle={() => onToggle(person.id)}
                />
              );
            })}
          </div>
        </>
      )}

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button type="button" onClick={onBack} className={`${OUTLINE_PLAN_BTN} shrink-0 gap-2 px-4`}>
          <ArrowLeft size={15} />
          Choose a different plan
        </button>

        {!tooSmall && (
          <button
            type="button"
            onClick={onContinue}
            disabled={busy || !canCheckout(selectedIds, household, limit)}
            className={`${PRIMARY_PLAN_BTN} shrink-0 gap-2 px-5 disabled:opacity-50`}
          >
            {continueLabel}
            <ArrowRight size={15} />
          </button>
        )}
      </div>
    </div>
  );
};

export default MemberPicker;
